import { useEffect } from "react";

interface SEOOptions {
  title: string;
  description: string;
  keywords?: string;
  canonical?: string;
  ogImage?: string;
  jsonLd?: Record<string, any> | Record<string, any>[];
}

const upsertMeta = (selector: string, attr: "name" | "property", key: string, content: string) => {
  let el = document.head.querySelector<HTMLMetaElement>(selector);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
};

const upsertLink = (rel: string, href: string) => {
  let el = document.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
};

/**
 * Dynamic per-route SEO. Updates document title, meta description,
 * keywords, canonical, OG/Twitter tags, and optionally injects JSON-LD.
 * Cleans up injected JSON-LD on unmount so previous-page schema doesn't leak.
 */
export const useSEO = ({ title, description, keywords, canonical, ogImage, jsonLd }: SEOOptions) => {
  useEffect(() => {
    const previousTitle = document.title;
    document.title = title;

    upsertMeta('meta[name="description"]', "name", "description", description);
    if (keywords) upsertMeta('meta[name="keywords"]', "name", "keywords", keywords);
    if (canonical) upsertLink("canonical", canonical);

    upsertMeta('meta[property="og:title"]', "property", "og:title", title);
    upsertMeta('meta[property="og:description"]', "property", "og:description", description);
    if (canonical) upsertMeta('meta[property="og:url"]', "property", "og:url", canonical);
    if (ogImage) upsertMeta('meta[property="og:image"]', "property", "og:image", ogImage);

    upsertMeta('meta[name="twitter:title"]', "name", "twitter:title", title);
    upsertMeta('meta[name="twitter:description"]', "name", "twitter:description", description);
    if (ogImage) upsertMeta('meta[name="twitter:image"]', "name", "twitter:image", ogImage);

    // hreflang self-reference (en) — primes future i18n expansion and signals
    // language to crawlers. Drop existing alternate links before re-adding.
    if (canonical) {
      document.head
        .querySelectorAll('link[rel="alternate"][hreflang]')
        .forEach((el) => el.remove());
      const alt = document.createElement("link");
      alt.setAttribute("rel", "alternate");
      alt.setAttribute("hreflang", "en");
      alt.setAttribute("href", canonical);
      document.head.appendChild(alt);
      const xDefault = document.createElement("link");
      xDefault.setAttribute("rel", "alternate");
      xDefault.setAttribute("hreflang", "x-default");
      xDefault.setAttribute("href", canonical);
      document.head.appendChild(xDefault);
    }

    const injected: HTMLScriptElement[] = [];
    if (jsonLd) {
      const items = Array.isArray(jsonLd) ? jsonLd : [jsonLd];
      items.forEach((data) => {
        const s = document.createElement("script");
        s.type = "application/ld+json";
        s.dataset.dynamicSeo = "true";
        s.textContent = JSON.stringify(data);
        document.head.appendChild(s);
        injected.push(s);
      });
    }

    return () => {
      document.title = previousTitle;
      injected.forEach((s) => s.remove());
    };
  }, [title, description, keywords, canonical, ogImage, JSON.stringify(jsonLd)]);
};
